import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { supabaseAdmin, supabase } from '../lib/supabaseClient'

// Tipos para dados administrativos
export interface AdminStats {
  totalUsuarios: number
  usuariosAtivos: number
  usuariosInativos: number
  usuariosTeste: number
  totalMovimentacoes: number
  movimentacoesMes: number
  receitaTotal: number
  receitaMes: number
  despesaTotal: number
  despesaMes: number
  totalPlanos: number
  planosAtivos: number
  totalChamados: number
  chamadosAbertos: number
  // Novas estatísticas de faturas
  totalFaturas: number
  faturasPagas: number
  faturasPendentes: number
  faturasVencidas: number
  receitaFaturas: number
  receitaFaturasMes: number
}

export interface UsuarioAdmin {
  id: string
  email: string
  nome?: string
  empresa_nome?: string
  whatsapp?: string
  created_at: string
  ativo: boolean
  is_teste: boolean
  admin: boolean
  plano_id?: string
  data_ativacao?: string
  final_teste?: string
}

export interface MovimentacaoAdmin {
  id: number
  valor: number
  descricao: string
  entrada: boolean
  data: string
  usuario: string
  comprovante?: string
  created_at: string
  usuario_info?: {
    nome?: string
    empresa_nome?: string
    email: string
  }
}

export interface PlanoAdmin {
  id: string
  nome: string
  descricao: string
  valor: number
  duracao_dias: number
  max_usuarios: number
  recursos: string[]
  ativo: boolean
  created_at: string
}

export interface ChamadoAdmin {
  id: string
  titulo: string // mapeado de "assunto"
  descricao: string
  usuario_id: string // mapeado de coluna "usuario"
  status: 'aberto' | 'em_andamento' | 'resolvido' | 'fechado'
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente'
  categoria: string
  created_at: string
  updated_at: string
  resposta?: string
  usuario_info?: {
    nome?: string
    email: string
    empresa_nome?: string
  }
}

interface AdminDataContextType {
  stats: AdminStats | null
  usuarios: UsuarioAdmin[]
  movimentacoes: MovimentacaoAdmin[]
  planos: PlanoAdmin[]
  chamados: ChamadoAdmin[]
  loading: boolean
  refreshData: () => Promise<void>
  loadUsuarios: (forceReload?: boolean) => Promise<void>
  loadMovimentacoes: () => Promise<void>
  loadPlanos: (forceReload?: boolean) => Promise<void>
  loadChamados: () => Promise<void>
  loadStats: (forceReload?: boolean) => Promise<void>
  clearCache: (key?: string) => void
  testarInsercaoFatura: () => Promise<void>
  responderChamado: (id: string, resposta: string) => Promise<void>
  atualizarStatusChamado: (id: string, status: ChamadoAdmin['status']) => Promise<void>
}

const AdminDataContext = createContext<AdminDataContextType | undefined>(undefined)

export const useAdminData = () => {
  const context = useContext(AdminDataContext)
  if (context === undefined) {
    throw new Error('useAdminData must be used within an AdminDataProvider')
  }
  return context
}

interface AdminDataProviderProps {
  children: ReactNode
}

export const AdminDataProvider: React.FC<AdminDataProviderProps> = ({ children }) => {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([])
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoAdmin[]>([])
  const [planos, setPlanos] = useState<PlanoAdmin[]>([])
  const [chamados, setChamados] = useState<ChamadoAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  // Funções para gerenciar cache
  const getCachedData = (key: string) => {
    try {
      const cached = localStorage.getItem(`admin_${key}`)
      if (cached) {
        const { data, timestamp } = JSON.parse(cached)
        // Cache válido por 5 minutos
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          console.log(`📦 [AdminData] Cache válido encontrado para ${key}:`, data)
          return data
        } else {
          console.log(`⏰ [AdminData] Cache expirado para ${key}`)
        }
      }
    } catch (error) {
      console.error('Erro ao ler cache:', error)
    }
    return null
  }

  const setCachedData = (key: string, data: unknown) => {
    try {
      localStorage.setItem(`admin_${key}`, JSON.stringify({
        data,
        timestamp: Date.now()
      }))
      console.log(`💾 [AdminData] Cache salvo para ${key}:`, data)
    } catch (error) {
      console.error('Erro ao salvar cache:', error)
    }
  }

  // Função para limpar cache manualmente
  const clearCache = (key?: string) => {
    try {
      if (key) {
        localStorage.removeItem(`admin_${key}`)
        console.log(`🧹 [AdminData] Cache limpo para ${key}`)
      } else {
        // Limpar todos os caches
        Object.keys(localStorage).forEach(k => {
          if (k.startsWith('admin_')) {
            localStorage.removeItem(k)
          }
        })
        console.log('🧹 [AdminData] Todos os caches foram limpos')
      }
    } catch (error) {
      console.error('Erro ao limpar cache:', error)
    }
  }

  // Função para testar inserção de fatura usando anon key primeiro
  const testarInsercaoFatura = async () => {
    try {
      console.log('🧪 [AdminData] Testando inserção de fatura...')
      
      // Buscar primeiro usuário e plano para usar como referência
      const { data: usuariosData } = await supabaseAdmin
        .from('usuario')
        .select('id')
        .limit(1)
      
      const { data: planosData } = await supabaseAdmin
        .from('plano')
        .select('id')
        .limit(1)
      
      if (!usuariosData?.[0]?.id || !planosData?.[0]?.id) {
        console.error('❌ [AdminData] Usuário ou plano não encontrado para teste')
        return
      }
      
             const faturaTeste = {
         usuario_id: usuariosData[0].id,
         plano_id: planosData[0].id,
         valor: 29.90,
         status_pagamento: 'pendente',
         data_vencimento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 dias
         data_pagamento: null,
         metodo_pagamento: null,
         comprovante: 'Fatura de teste criada via admin',
         observacoes: 'Fatura criada para teste do sistema'
       }
      
      console.log('📝 [AdminData] Tentando inserir fatura de teste:', faturaTeste)
      
      // Tentar primeiro com anon key
      let { data: faturaInserida, error: erroInsercao } = await supabase
        .from('fatura')
        .insert([faturaTeste])
        .select()
      
      if (erroInsercao) {
        console.log('⚠️ [AdminData] Anon key falhou na inserção, tentando com service role...')
        const { data: adminData, error: adminError } = await supabaseAdmin
          .from('fatura')
          .insert([faturaTeste])
          .select()
        
        if (adminError) {
          console.error('❌ [AdminData] Erro ao inserir fatura de teste:', adminError)
          return
        } else {
          faturaInserida = adminData
          console.log('✅ [AdminData] Fatura de teste criada com service role:', faturaInserida)
        }
      } else {
        console.log('✅ [AdminData] Fatura de teste criada com anon key:', faturaInserida)
      }
      
      // Recarregar estatísticas para mostrar a nova fatura
      await loadStats(true)
      
    } catch (error) {
      console.error('💥 [AdminData] Erro ao testar inserção:', error)
    }
  }

  // Configurar realtime subscriptions
  const setupRealtimeSubscriptions = () => {
    // Subscription para usuários
    const usuariosSubscription = supabaseAdmin
      .channel('usuarios_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'usuario' },
        (payload) => {
          console.log('👥 [Realtime] Mudança detectada em usuários:', payload.eventType)
          // Recarregar apenas usuários quando houver mudanças
          loadUsuarios(true) // true = force reload
        }
      )
      .subscribe()

    // Subscription para planos
    const planosSubscription = supabaseAdmin
      .channel('planos_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'plano' },
        (payload) => {
          console.log('💳 [Realtime] Mudança detectada em planos:', payload.eventType)
          // Recarregar apenas planos quando houver mudanças
          loadPlanos(true) // true = force reload
        }
      )
      .subscribe()

    // Subscription para faturas
    const faturasSubscription = supabaseAdmin
      .channel('faturas_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'fatura' },
        (payload) => {
          console.log('📄 [Realtime] Mudança detectada em faturas:', payload.eventType)
          // Recarregar apenas estatísticas quando houver mudanças
          loadStats(true) // true = force reload
        }
      )
      .subscribe()

    // Subscription para chamados
    const chamadosSubscription = supabaseAdmin
      .channel('chamados_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'chamado' },
        () => {
          // Recarregar chamados quando houver mudanças
          loadChamados()
        }
      )
      .subscribe()

    // Retornar funções para limpar subscriptions
    return () => {
      usuariosSubscription.unsubscribe()
      planosSubscription.unsubscribe()
      faturasSubscription.unsubscribe()
      chamadosSubscription.unsubscribe()
    }
  }

  // Carregar estatísticas gerais
  const loadStats = useCallback(async (forceReload = false) => {
    try {
      console.log('🔄 [AdminData] Iniciando loadStats, forceReload:', forceReload)
      
      // Tentar usar cache primeiro (se não for force reload)
      if (!forceReload) {
        const cached = getCachedData('stats')
        if (cached) {
          console.log('📦 [AdminData] Usando cache de stats:', cached)
          setStats(cached)
          return
        }
      }

      setLoading(true)

      // 1. Estatísticas de usuários
      const { data: usuariosData, error: usuariosError } = await supabaseAdmin
        .from('usuario')
        .select('*')

      if (usuariosError) {
        console.error('❌ [AdminData] Erro ao carregar usuários:', usuariosError)
        return
      }

      const totalUsuarios = usuariosData?.length || 0
      const usuariosAtivos = usuariosData?.filter(u => u.ativo)?.length || 0
      const usuariosInativos = usuariosData?.filter(u => !u.ativo)?.length || 0
      const usuariosTeste = usuariosData?.filter(u => u.is_teste)?.length || 0

      // 2. Estatísticas de movimentações
      const { data: movimentacoesData, error: movimentacoesError } = await supabaseAdmin
        .from('movimentacoes')
        .select('*')

      if (movimentacoesError) {
        console.error('❌ [AdminData] Erro ao carregar movimentações:', movimentacoesError)
        return
      }

      const totalMovimentacoes = movimentacoesData?.length || 0
      
      // Movimentações do mês atual
      const hoje = new Date()
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
      const movimentacoesMes = movimentacoesData?.filter(m => 
        new Date(m.data) >= inicioMes
      )?.length || 0

      // Cálculos financeiros
      const receitaTotal = movimentacoesData
        ?.filter(m => m.entrada)
        ?.reduce((sum, m) => sum + Number(m.valor), 0) || 0

      const despesaTotal = movimentacoesData
        ?.filter(m => !m.entrada)
        ?.reduce((sum, m) => sum + Number(m.valor), 0) || 0

      const receitaMes = movimentacoesData
        ?.filter(m => m.entrada && new Date(m.data) >= inicioMes)
        ?.reduce((sum, m) => sum + Number(m.valor), 0) || 0

      const despesaMes = movimentacoesData
        ?.filter(m => !m.entrada && new Date(m.data) >= inicioMes)
        ?.reduce((sum, m) => sum + Number(m.valor), 0) || 0

      // 3. Estatísticas de planos (buscar dados reais)
      const { data: planosData, error: planosError } = await supabaseAdmin
        .from('plano')
        .select('*')

      if (planosError) {
        console.error('❌ [AdminData] Erro ao carregar planos:', planosError)
        return
      }

      const totalPlanos = planosData?.length || 0
      const planosAtivos = planosData?.filter(p => p.ativo)?.length || 0

      // 4. Estatísticas de chamados (por enquanto mock)
      const totalChamados = 4
      const chamadosAbertos = 1

                   // 5. Estatísticas de faturas
       console.log('📄 [AdminData] Carregando estatísticas de faturas...')
       
       // Fazer consulta direta para verificar se há dados
       console.log('🔍 [AdminData] Verificando dados da tabela fatura...')
       
       // Tentar primeiro com anon key (que tem permissão SELECT)
       console.log('🔑 [AdminData] Tentando com anon key primeiro...')
       let { data: faturasData, error: faturasError } = await supabase
         .from('fatura')
         .select('*')
       // Normalizar retorno possivelmente nulo para array vazio
       faturasData = faturasData ?? []
       
       // Se anon key não funcionar, tentar com service role
       if (false && (faturasError || ((faturasData ?? []) as any[]).length === 0)) {
         console.log('⚠️ [AdminData] Anon key falhou, tentando com service role...')
         const { data: adminData, error: adminError } = await supabaseAdmin
           .from('fatura')
           .select('*')
         
         const safeAdminData = (adminData ?? []) as any[]
         if (safeAdminData.length > 0) {
           console.log('✅ [AdminData] Service role funcionou!')
           faturasData = safeAdminData
           faturasError = null
         } else {
           console.log('❌ [AdminData] Service role também falhou:', adminError)
         }
       } else {
         console.log('✅ [AdminData] Anon key funcionou perfeitamente!')
       }
       
       // Se ainda não funcionar, tentar diferentes nomes de tabela
       if (false && (faturasError || ((faturasData ?? []) as any[]).length === 0)) {
         console.log('⚠️ [AdminData] Ambas as keys falharam, tentando nomes alternativos...')
         
         const tabelasPossiveis = ['fatura', 'faturas', 'invoice', 'invoices']
         
         for (const tabela of tabelasPossiveis) {
           try {
             console.log(`🔍 [AdminData] Tentando tabela: ${tabela} com anon key`)
             const { data: altData, error: altError } = await supabase
               .from(tabela)
               .select('*')
               .limit(1)
             
             if (((altData ?? []) as any[]).length > 0) {
               console.log(`✅ [AdminData] Tabela ${tabela} encontrada com anon key!`)
               faturasData = (altData ?? []) as any[]
               faturasError = null
               break
             }
           } catch (err) {
             console.log(`❌ [AdminData] Tabela ${tabela} não existe`)
           }
         }
       }

       console.log('📊 [AdminData] Query de faturas resultado:', { faturasData, faturasError })
       
                // Log detalhado para debug
         if (((faturasData ?? []) as any[]).length > 0) {
           console.log('📋 [AdminData] Primeira fatura encontrada:', faturasData[0])
           console.log('🔍 [AdminData] Campos disponíveis:', Object.keys(faturasData[0]))
           console.log('💰 [AdminData] Valor da primeira fatura:', faturasData[0].valor)
           console.log('📅 [AdminData] Status da primeira fatura:', faturasData[0].status_pagamento)
         } else {
         console.log('⚠️ [AdminData] Tabela fatura está vazia - não há registros')
         
         // Tentar consulta alternativa para debug
         console.log('🔍 [AdminData] Tentando consulta alternativa...')
         const { data: debugData, error: debugError } = await supabaseAdmin
           .from('fatura')
           .select('count')
         
         console.log('🔍 [AdminData] Debug count:', { debugData, debugError })
       }

       if (faturasError) {
         console.error('❌ [AdminData] Erro ao carregar faturas:', faturasError)
         // Se a tabela não existir ainda, usar valores padrão
       } else {
         console.log('✅ [AdminData] Faturas carregadas com sucesso:', faturasData?.length || 0, 'registros')
         if (((faturasData ?? []) as any[]).length > 0) {
           console.log('📋 [AdminData] Primeira fatura:', faturasData[0])
           console.log('🔍 [AdminData] Campos disponíveis:', Object.keys(faturasData[0]))
         } else {
           console.log('⚠️ [AdminData] Tabela fatura está vazia - não há registros')
         }
       }

             const totalFaturas = faturasData?.length || 0
       const faturasPagas = faturasData?.filter(f => f.status_pagamento === 'pago')?.length || 0
       const faturasPendentes = faturasData?.filter(f => f.status_pagamento === 'pendente')?.length || 0
       const faturasVencidas = faturasData?.filter(f => f.status_pagamento === 'vencido')?.length || 0

       // Receita das faturas (apenas faturas pagas)
       const receitaFaturas = faturasData
         ?.filter(f => f.status_pagamento === 'pago')
         ?.reduce((sum, f) => sum + Number(f.valor), 0) || 0

       // Receita das faturas do mês atual
       const receitaFaturasMes = faturasData
         ?.filter(f => f.status_pagamento === 'pago' && new Date(f.data_pagamento) >= inicioMes)
         ?.reduce((sum, f) => sum + Number(f.valor), 0) || 0

      const statsData = {
        totalUsuarios,
        usuariosAtivos,
        usuariosInativos,
        usuariosTeste,
        totalMovimentacoes,
        movimentacoesMes,
        receitaTotal,
        receitaMes,
        despesaTotal,
        despesaMes,
        totalPlanos,
        planosAtivos,
        totalChamados,
        chamadosAbertos,
        // Novas estatísticas de faturas
        totalFaturas,
        faturasPagas,
        faturasPendentes,
        faturasVencidas,
        receitaFaturas,
        receitaFaturasMes
      }

      setStats(statsData)
      
      // Salvar no cache
      setCachedData('stats', statsData)

          } catch (error) {
        console.error('💥 [AdminData] Erro geral ao carregar estatísticas:', error)
      } finally {
        setLoading(false)
      }
    }, [])

  // Carregar lista de usuários
  const loadUsuarios = useCallback(async (forceReload = false) => {
    try {
      console.log('🔄 [AdminData] Iniciando loadUsuarios, forceReload:', forceReload)
      
      // Tentar usar cache primeiro (se não for force reload)
      if (!forceReload) {
        const cached = getCachedData('usuarios')
        if (cached) {
          console.log('📦 [AdminData] Usando cache de usuários:', cached.length, 'registros')
          setUsuarios(cached)
          return
        }
      }

      console.log('🌐 [AdminData] Fazendo query no Supabase...')
      
      // Usar service role key para bypass total do RLS
      const { data: usuariosData, error: usuariosError } = await supabaseAdmin
        .from('usuario')
        .select('*')
        .order('created_at', { ascending: false })

      if (usuariosError) {
        console.error('❌ [AdminData] Erro ao carregar usuários:', usuariosError)
        return
      }

      console.log('👥 [AdminData] Usuários carregados do Supabase:', usuariosData?.length || 0, 'registros')
      console.log('📊 [AdminData] Dados dos usuários:', usuariosData)
      
      setUsuarios(usuariosData || [])
      
      // Salvar no cache
      setCachedData('usuarios', usuariosData || [])
      console.log('💾 [AdminData] Cache de usuários atualizado')
      
    } catch (error) {
      console.error('💥 [AdminData] Erro ao carregar usuários:', error)
    }
  }, [])

  // Carregar movimentações com dados do usuário
  const loadMovimentacoes = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('movimentacoes')
        .select(`
          *,
          usuario_info:usuario(id, nome, empresa_nome, email)
        `)
        .order('created_at', { ascending: false })
        .limit(100) // Limitar para performance

      if (error) {
        console.error('❌ [AdminData] Erro ao carregar movimentações:', error)
        return
      }

      setMovimentacoes(data || [])
    } catch (error) {
      console.error('💥 [AdminData] Erro ao carregar movimentações:', error)
    }
  }

  // Carregar planos da tabela real "plano"
  const loadPlanos = async (forceReload = false) => {
    try {
      // Tentar usar cache primeiro (se não for force reload)
      if (!forceReload) {
        const cached = getCachedData('planos')
        if (cached) {
          setPlanos(cached)
          return
        }
      }

      const { data, error } = await supabaseAdmin
        .from('plano')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ [AdminData] Erro ao carregar planos:', error)
        return
      }

      // Converter dados da tabela para o formato esperado
      const planosConvertidos: PlanoAdmin[] = (data || []).map(plano => ({
        id: plano.id,
        nome: plano.nome || 'Sem nome',
        descricao: plano.descricao || 'Sem descrição',
        valor: Number(plano.valor) || 0,
        duracao_dias: Number(plano.duracao_dias) || 30,
        max_usuarios: 5, // Campo não existe na tabela, usar valor padrão
        recursos: Array.isArray(plano.recursos) ? plano.recursos : ['Recursos básicos'],
        ativo: plano.ativo || false,
        created_at: plano.created_at || new Date().toISOString()
      }))

      setPlanos(planosConvertidos)
      
      // Salvar no cache
      setCachedData('planos', planosConvertidos)
    } catch (error) {
      console.error('💥 [AdminData] Erro ao carregar planos:', error)
    }
  }

  // Carregar chamados da tabela real "chamado"
  const loadChamados = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('chamado')
        .select(`
          id,
          assunto,
          descricao,
          categoria,
          prioridade,
          status,
          created_at,
          updated_at,
          resposta,
          usuario,
          usuario_info:usuario(id, nome, email, empresa_nome)
        `)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ [AdminData] Erro ao carregar chamados:', error)
        return
      }

      const mapped: ChamadoAdmin[] = (data || []).map((c: any) => ({
        id: String(c.id),
        titulo: c.assunto || '(Sem assunto)',
        descricao: c.descricao || '',
        usuario_id: c.usuario || c.usuario_info?.id || '',
        status: (c.status || 'aberto'),
        prioridade: (c.prioridade || 'normal'),
        categoria: c.categoria || 'Outros',
        created_at: c.created_at,
        updated_at: c.updated_at || c.created_at,
        resposta: c.resposta || undefined,
        usuario_info: c.usuario_info ? {
          nome: c.usuario_info.nome || undefined,
          email: c.usuario_info.email,
          empresa_nome: c.usuario_info.empresa_nome || undefined
        } : undefined
      }))

      setChamados(mapped)
    } catch (error) {
      console.error('💥 [AdminData] Erro ao carregar chamados:', error)
    }
  }

  const responderChamado = async (id: string, resposta: string) => {
    try {
      const { error } = await supabaseAdmin
        .from('chamado')
        .update({ resposta, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) {
        console.error('❌ [AdminData] Erro ao responder chamado:', error)
        throw error
      }

      await loadChamados()
    } catch (error) {
      console.error('💥 [AdminData] Erro ao responder chamado:', error)
      throw error
    }
  }

  const atualizarStatusChamado = async (id: string, status: ChamadoAdmin['status']) => {
    try {
      const { error } = await supabaseAdmin
        .from('chamado')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) {
        console.error('❌ [AdminData] Erro ao atualizar status do chamado:', error)
        throw error
      }

      await loadChamados()
    } catch (error) {
      console.error('💥 [AdminData] Erro ao atualizar status:', error)
      throw error
    }
  }

  // Função para atualizar todos os dados (force reload)
  const refreshData = useCallback(async () => {
    console.log('🔄 [AdminData] Forçando reload de todos os dados...')
    await Promise.all([
      loadStats(true),      // Force reload
      loadUsuarios(true),   // Force reload
      loadMovimentacoes(),
      loadPlanos(true),     // Force reload
      loadChamados()
    ])
  }, [loadStats, loadUsuarios, loadMovimentacoes, loadPlanos, loadChamados])

  // Carregar dados iniciais apenas uma vez e configurar realtime
  useEffect(() => {
    // Evitar múltiplas inicializações
    if (initialized) return
    
    console.log('🚀 [AdminData] Iniciando carregamento inicial...')
    
    // Carregar dados iniciais
    refreshData()
    
    // Configurar realtime subscriptions
    const cleanup = setupRealtimeSubscriptions()
    
    setInitialized(true)
    
    // Cleanup function para limpar subscriptions quando o componente for desmontado
    return cleanup
  }, []) // Dependências vazias = executa apenas uma vez

  const value: AdminDataContextType = {
    stats,
    usuarios,
    movimentacoes,
    planos,
    chamados,
    loading,
    refreshData,
    loadUsuarios,
    loadMovimentacoes,
    loadPlanos,
    loadChamados,
    loadStats,
    clearCache,
    testarInsercaoFatura,
    responderChamado,
    atualizarStatusChamado
  }

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  )
}









